import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme, CssBaseline } from '@mui/material';

const STORAGE_KEY = 'ds_theme';
const FONT_KEY = 'ds_font';

// Predefined theme presets
export const themePresets = [
  {
    id: 'default',
    name: 'Default Blue',
    mode: 'light',
    primary: { main: '#1976d2' },
    secondary: { main: '#9c27b0' },
    background: { default: '#f7f9fc', paper: '#ffffff' }
  },
  {
    id: 'ocean',
    name: 'Ocean',
    mode: 'light',
    primary: { main: '#0288d1' },
    secondary: { main: '#00bcd4' },
    background: { default: '#f4fbfd', paper: '#ffffff' }
  },
  {
    id: 'emerald',
    name: 'Emerald',
    mode: 'light',
    primary: { main: '#2e7d32' },
    secondary: { main: '#66bb6a' },
    background: { default: '#f4fbf6', paper: '#ffffff' }
  },
  {
    id: 'violet',
    name: 'Violet',
    mode: 'light',
    primary: { main: '#673ab7' },
    secondary: { main: '#7c4dff' },
    background: { default: '#f7f5ff', paper: '#ffffff' }
  },
  {
    id: 'sunset',
    name: 'Sunset',
    mode: 'light',
    primary: { main: '#ef6c00' },
    secondary: { main: '#ff7043' },
    background: { default: '#fff8f1', paper: '#ffffff' }
  },
  {
    id: 'slate',
    name: 'Slate',
    mode: 'light',
    primary: { main: '#455a64' },
    secondary: { main: '#78909c' },
    background: { default: '#f5f7f9', paper: '#ffffff' }
  },
  {
    id: 'dark',
    name: 'Midnight',
    mode: 'dark',
    primary: { main: '#90caf9' },
    secondary: { main: '#ce93d8' },
    background: { default: '#0b0f19', paper: '#111827' }
  },
];

// Font presets (Google Fonts)
export const fontPresets = [
  {
    id: 'inter',
    name: 'Inter',
    import: 'Inter:wght@300;400;500;600;700',
    stack: "Inter, Roboto, Helvetica, Arial, sans-serif",
  },
  {
    id: 'poppins',
    name: 'Poppins',
    import: 'Poppins:wght@300;400;500;600;700',
    stack: "Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    import: 'Montserrat:wght@300;400;500;600;700',
    stack: "Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  {
    id: 'manrope',
    name: 'Manrope',
    import: 'Manrope:wght@300;400;500;600;700',
    stack: "Manrope, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  {
    id: 'mulish',
    name: 'Mulish',
    import: 'Mulish:wght@300;400;500;600;700',
    stack: "Mulish, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    import: 'Merriweather:wght@300;400;700',
    stack: "Merriweather, Georgia, 'Times New Roman', Times, serif",
  },
];

const ThemeContext = createContext({
  themeId: 'default',
  setThemeId: () => {},
  presets: themePresets,
  fontId: 'inter',
  setFontId: () => {},
  fonts: fontPresets,
});

export const useThemeSettings = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'default';
    } catch {
      return 'default';
    }
  });
  const [fontId, setFontId] = useState(() => {
    try {
      return localStorage.getItem(FONT_KEY) || 'inter';
    } catch {
      return 'inter';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, themeId);
    } catch {}
  }, [themeId]);

  useEffect(() => {
    try {
      localStorage.setItem(FONT_KEY, fontId);
    } catch {}
  }, [fontId]);

  // Dynamically load the selected Google Font
  useEffect(() => {
    const font = fontPresets.find(f => f.id === fontId) || fontPresets[0];
    const id = 'ds-google-font';
    const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font.import)}&display=swap`;
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;
  }, [fontId]);

  const preset = themePresets.find(p => p.id === themeId) || themePresets[0];

  const theme = useMemo(() => createTheme({
    palette: {
      mode: preset.mode,
      primary: preset.primary,
      secondary: preset.secondary,
      background: preset.background,
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: { root: { textTransform: 'none', borderRadius: 10 } },
      },
      MuiPaper: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
    },
    typography: {
      fontFamily: (fontPresets.find(f => f.id === fontId) || fontPresets[0]).stack,
    },
  }), [preset, fontId]);

  const value = useMemo(() => ({
    themeId,
    setThemeId,
    presets: themePresets,
    fontId,
    setFontId,
    fonts: fontPresets,
  }), [themeId, fontId]);

  return (
    <ThemeContext.Provider value={value}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
